import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import i18next from 'i18next'

import { NO_VALUE } from '~/models/jepxFee'

/**
 * 解決できなかった事業者名・組織名の表示 (AC-IJM-004 / AC-IJM-005)。
 * backend は未解決を空文字で返し、 文言はここで与える。
 */
export function resolvedNameLabel(name: string): string {
  return name === '' ? i18next.t('page:adminJepxMemberInfo.unresolved') : name
}

/**
 * 絶対時刻 → **JST 壁時計**の `YYYY/MM/DD HH:MM`。 未設定は em dash。
 *
 * 共有の `formatDateTimeFull` も #6074 で JST 化され同じ出力になる (この page 独自の
 * 実装は未統合)。 料金・手数料の最終更新は規程の反映日
 * (JST 0 時) 固定なので、 ローカル基準だと UTC 端末で前日にずれて見える。
 */
export function formatUpdatedAt(ts: Timestamp | undefined): string {
  if (ts === undefined) {
    return NO_VALUE
  }
  const parts = new Intl.DateTimeFormat('ja-JP', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
  }).formatToParts(timestampDate(ts))
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? ''
  // hour は hour12:false でも locale により 24 が出るため 0 に丸める。
  const hour = part('hour') === '24' ? '00' : part('hour')
  return `${part('year')}/${part('month')}/${part('day')} ${hour}:${part('minute')}`
}

/**
 * google.type.Date を `YYYY/MM/DD` にする (反映日)。 未設定は em dash。
 *
 * `Date` に載せ替えずに year / month / day をそのまま並べる: 反映日は暦日で
 * あってタイムスタンプではないため、 端末 TZ で 1 日ずれる経路を作らない。
 */
export function formatPublishedOn(date: ProtoDate | undefined): string {
  if (date === undefined) {
    return NO_VALUE
  }
  const mm = String(date.month).padStart(2, '0')
  const dd = String(date.day).padStart(2, '0')
  return `${date.year}/${mm}/${dd}`
}

/** 年会費 (円/年) を 3 桁区切りで表示する。 */
export function formatAnnualFeeYen(value: bigint): string {
  return value.toLocaleString('ja-JP')
}
